import React, { useState, useEffect } from 'react';
import AgentPropertyList from './AgentPropertyList';
import AgentPropertyForm from './AgentPropertyForm';
import AgentDashboard from './AgentDashboard';
import AgentLogin from './AgentLogin';
import AgentLayout from './AgentLayout';
import AgentOnboardingModal from './AgentOnboardingModal';
import AgentInvitations from './AgentInvitations';
import AgentShowingsTab from './AgentShowingsTab';
import AgentConversations from './AgentConversations';
import AvailableLeads from './AvailableLeads';
import MediaLibrary from './MediaLibrary';
import AgentTeamRoster from './AgentTeamRoster';
import SupportHub from './support/SupportHub';
import SupportCategory from './support/SupportCategory';
import SupportArticle from './support/SupportArticle';
import { Plus } from 'react-feather';

import AuthService from '../../services/authService';
import propertyService from '../../services/propertyService';
import '../admin/AdminDashboard.css';

const AgentPortal = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [propertyCount, setPropertyCount] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [currentProperty, setCurrentProperty] = useState(null);
  const [agentInfo, setAgentInfo] = useState(null);
  const [showTrialWelcome, setShowTrialWelcome] = useState(false);
  const [supportCategory, setSupportCategory] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [supportArticle, setSupportArticle] = useState(null);
  
  // Check if token exists and user is an agent on page load
  useEffect(() => {
    const checkAuth = () => {
      const user = AuthService.getCurrentUser();
      if (user && user.role === 'agent') {
        setIsAuthenticated(true);
        setAgentInfo(user);
        // Show trial welcome modal for free trial users
        if (user?.agentInfo?.plan === 'Free Trial') {
          setShowTrialWelcome(true);
        }
      } else {
        // Clear auth if user is not an agent
        if (user && user.role !== 'agent') {
          AuthService.logout();
        }
        setIsAuthenticated(false);
      }
      setIsCheckingAuth(false);
    };
    
    checkAuth();
  }, []);
  
  // Fetch property count when authenticated
  useEffect(() => {
    if (isAuthenticated && agentInfo) {
      fetchAgentPropertyCount();
    }
  }, [isAuthenticated, agentInfo]);

  // Onboarding status. Drives the forced setup modal — if this comes back with
  // anything other than status 'complete', the agent can't use the portal until
  // they finish. Deliberately fetched from the server rather than read off the
  // cached user, so a stale localStorage blob can't let someone skip setup.
  useEffect(() => {
    if (!isAuthenticated || !agentInfo) return;

    const fetchOnboarding = async () => {
      try {
        const response = await AuthService.fetchWithAuth(
          'https://back-end-x7tx.onrender.com/api/auth/onboarding/me'
        );
        if (!response) return; // fetchWithAuth bounced a 401
        const data = await response.json();
        if (data.success) {
          setOnboarding(data.data);
        }
      } catch (error) {
        // Fail open: if we can't reach the endpoint, don't lock the agent out of
        // their own portal over a network blip.
        console.error('Error fetching onboarding status:', error);
      }
    };

    fetchOnboarding();
  }, [isAuthenticated, agentInfo]);

  // An onboarding step finished, but there are more to go.
  const handleOnboardingStep = (result) => {
    setOnboarding(prev => ({
      ...prev,
      completedSteps: result.completedSteps,
      nextStep: result.nextStep,
      status: result.status
    }));
  };

  // Last step done — drop the modal and refresh the cached agent so the sidebar
  // avatar picks up the new photo.
  const handleOnboardingComplete = (result) => {
    setOnboarding(prev => ({
      ...prev,
      completedSteps: result.completedSteps,
      nextStep: null,
      status: 'complete'
    }));
    setAgentInfo(AuthService.getCurrentUser());
  };

  // Forced setup. Rendered above every tab, not just the dashboard — there's no
  // close button, so leaving a tab uncovered would be an escape hatch.
  const OnboardingGate = () => (
    <AgentOnboardingModal
      onboarding={onboarding}
      onStepComplete={handleOnboardingStep}
      onAllComplete={handleOnboardingComplete}
    />
  );
  
  const fetchAgentPropertyCount = async () => {
    try {
      // Use agent ID from the authenticated user
      const agentId = agentInfo._id;
      const response = await propertyService.getAgentPropertyCount(agentId);
      
      if (response && response.count !== undefined) {
        setPropertyCount(response.count);
      }
    } catch (error) {
      console.error('Error fetching agent property count:', error);
    }
  };
  
  const handleLoginSuccess = (userData) => {
    setIsAuthenticated(true);
    setAgentInfo(userData);
    // Show trial welcome modal for free trial users
    if (userData?.agentInfo?.plan === 'Free Trial') {
      setShowTrialWelcome(true);
    }
  };

  const handleLogout = () => {
    AuthService.logout();
    setIsAuthenticated(false);
    setAgentInfo(null);
    window.location.href = '/'; // Change this to your main login page URL if different
  };

  const handleEditProperty = (property) => {
    setCurrentProperty(property);
    setActiveTab('edit');
  };

  const handlePropertyAdded = () => {
    fetchAgentPropertyCount();
    setActiveTab('listings');
    setCurrentProperty(null);
  };
  
  const handleAddNewProperty = () => {
    setCurrentProperty(null);
    if (activeTab !== 'add') {
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveTab('add');
        setIsTransitioning(false);
      }, 300);
    }
  };
  
  const handleMyListings = () => {
    if (activeTab !== 'listings') {
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveTab('listings');
        setIsTransitioning(false);
      }, 300);
    }
  };
  
  const handleInvitations = () => {
    console.log('Invitations navigation called');
    if (activeTab !== 'invitations') {
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveTab('invitations');
        setIsTransitioning(false);
      }, 300);
    }
  };

  const handleDashboard = () => {
    console.log('Dashboard navigation called');
    if (activeTab !== 'dashboard') {
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveTab('dashboard');
        setIsTransitioning(false);
      }, 300);
    }
  };
  
  const handleShowings = () => {
    console.log('Showings navigation called');
    if (activeTab !== 'showings') {
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveTab('showings');
        setIsTransitioning(false);
      }, 300);
    }
  };

  const handleMediaLibrary = () => {
    console.log('Media Library navigation called');
    if (activeTab !== 'media') {
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveTab('media');
        setIsTransitioning(false);
      }, 300);
    }
  };
  
  const handleConversations = () => {
    console.log('Conversations navigation called');
    if (activeTab !== 'conversations') {
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveTab('conversations');
        setIsTransitioning(false);
      }, 300);
    }
  };
  
  const handleAvailableLeads = () => {
    console.log('Available Leads navigation called');
    if (activeTab !== 'available-leads') {
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveTab('available-leads');
        setIsTransitioning(false);
      }, 300);
    }
  };
  
  const handleTeamRoster = () => {
    console.log('Team Roster navigation called');
    if (activeTab !== 'team-roster') {
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveTab('team-roster');
        setIsTransitioning(false);
      }, 300);
    }
  };

  const handleSupportHub = () => {
    console.log('Support Hub navigation called');
    setSupportCategory(null);
    setSupportArticle(null);
    if (activeTab !== 'support-hub') {
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveTab('support-hub');
        setIsTransitioning(false);
      }, 300);
    }
  };

  const handleSupportCategory = (categorySlug) => {
    console.log('Support Category navigation called:', categorySlug);
    setIsTransitioning(true);
    setSupportCategory(categorySlug);
    setSupportArticle(null);
    setTimeout(() => {
      setActiveTab('support-category');
      setIsTransitioning(false);
    }, 300);
  };

  const handleSupportArticle = (categorySlug, articleSlug) => {
    console.log('Support Article navigation called:', categorySlug, articleSlug);
    setIsTransitioning(true);
    setSupportCategory(categorySlug);
    setSupportArticle(articleSlug);
    setTimeout(() => {
      setActiveTab('support-article');
      setIsTransitioning(false);
    }, 300);
  };
  
  // Show loading state while checking auth
  if (isCheckingAuth) {
    return (
      <div className="loading-state" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div className="spinner"></div>
        <p style={{ marginLeft: '12px' }}>Loading...</p>
      </div>
    );
  }
  
  // Show login form if not authenticated
  if (!isAuthenticated) {
    return <AgentLogin onLoginSuccess={handleLoginSuccess} />;
  }

  // Global animation styles
  const animationStyles = `
    @keyframes slideInFromLeft {
      from {
        opacity: 0;
        transform: translateX(-30px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }
    @keyframes fadeOut {
      from {
        opacity: 1;
      }
      to {
        opacity: 0;
      }
    }
    .page-transition {
      animation: slideInFromLeft 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    }
    .page-transition-out {
      animation: fadeOut 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      opacity: 0;
    }
  `;

  // Trial Welcome Modal Component
  const TrialWelcomeModal = () => {
    if (!showTrialWelcome) return null;
    
    return (
      <>
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px'
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#1a1a1a',
              borderRadius: '16px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
              position: 'relative'
            }}
          >
            {/* Animated Gradient Background */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(147, 51, 234, 0.1) 50%, rgba(59, 130, 246, 0.1) 100%)',
              backgroundSize: '200% 200%',
              animation: 'gradientShift 8s ease infinite',
              pointerEvents: 'none',
              zIndex: 0
            }} />

            {/* Floating Particles */}
            {[...Array(15)].map((_, i) => (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  width: Math.random() * 6 + 2 + 'px',
                  height: Math.random() * 6 + 2 + 'px',
                  borderRadius: '50%',
                  background: `rgba(${99 + Math.random() * 50}, ${102 + Math.random() * 50}, 241, ${0.3 + Math.random() * 0.4})`,
                  left: Math.random() * 100 + '%',
                  top: Math.random() * 100 + '%',
                  animation: `float${i % 3} ${8 + Math.random() * 8}s ease-in-out infinite`,
                  animationDelay: `${Math.random() * 5}s`,
                  pointerEvents: 'none',
                  zIndex: 0
                }}
              />
            ))}

            {/* Content wrapper */}
            <div style={{ 
              position: 'relative', 
              zIndex: 1, 
              display: 'flex', 
              flexDirection: 'column', 
              height: '100%',
              overflow: 'hidden'
            }}>
              {/* Modal Header */}
              <div style={{
                padding: '24px',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                backgroundColor: 'transparent',
                flexShrink: 0
              }}>
                {/* Celebration Emoji */}
                <div style={{
                  width: '70px',
                  height: '70px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.3) 0%, rgba(147, 51, 234, 0.3) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '16px',
                  fontSize: '32px',
                  border: '1px solid rgba(99, 102, 241, 0.3)'
                }}>
                  🎉
                </div>
                <h2 style={{ margin: 0, fontSize: '1.5rem', color: '#ffffff', textAlign: 'center' }}>
                  Welcome to Your Free Trial!
                </h2>
              </div>

              {/* Modal Body */}
              <div style={{ 
                overflowY: 'auto', 
                padding: '24px',
                flex: 1,
                backgroundColor: 'transparent',
                minHeight: 0
              }}>
                <p style={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '15px',
                  lineHeight: '1.6',
                  marginBottom: '24px',
                  marginTop: 0
                }}>
                  Here's what you can do during your trial:
                </p>
                
                <div style={{ marginBottom: '8px' }}>
                  {[
                    { icon: '👀', text: 'See local user activity in your territories' },
                    { icon: '📧', text: 'Get notified when new users register nearby' },
                    { icon: '🧭', text: 'Explore all agent portal features' },
                    { icon: '📈', text: 'Convert to a paid account to claim & interact with users' }
                  ].map((item, index) => (
                    <div 
                      key={index} 
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        marginBottom: '16px',
                        gap: '14px',
                        padding: '12px 16px',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}
                    >
                      <span style={{ fontSize: '20px', flexShrink: 0 }}>{item.icon}</span>
                      <span style={{
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontSize: '14px',
                        lineHeight: '1.5'
                      }}>
                        {item.text}
                      </span>
                    </div>
                  ))}
                </div>

                <p style={{
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontSize: '13px',
                  marginTop: '20px',
                  marginBottom: 0,
                  textAlign: 'center'
                }}>
                  Questions? <a 
                    href="https://calendly.com/team-lauft/demo" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    style={{ color: '#a5b4fc', textDecoration: 'none' }}
                  >
                    Book a demo
                  </a> with our team.
                </p>
              </div>

              {/* Modal Footer */}
              <div style={{
                padding: '24px',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                backgroundColor: 'transparent',
                flexShrink: 0
              }}>
                <button
                  onClick={() => setShowTrialWelcome(false)}
                  style={{
                    padding: '10px 24px',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    color: '#ffffff',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                  }}
                >
                  Got it, let's go!
                </button>
                <a
                  href="https://buy.stripe.com/eVqaEY1u80Hmdq6dXK3F600"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: '10px 24px',
                    backgroundColor: 'rgba(99, 102, 241, 0.2)',
                    border: '1px solid rgba(99, 102, 241, 0.4)',
                    borderRadius: '8px',
                    color: '#a5b4fc',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    textDecoration: 'none',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'rgba(99, 102, 241, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'rgba(99, 102, 241, 0.2)';
                  }}
                >
                  Activate Account
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* CSS Animations */}
        <style>{`
          @keyframes gradientShift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }

          @keyframes float0 {
            0%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 0.3; }
            33% { transform: translate(30px, -30px) rotate(120deg); opacity: 0.6; }
            66% { transform: translate(-20px, 20px) rotate(240deg); opacity: 0.4; }
          }

          @keyframes float1 {
            0%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 0.4; }
            33% { transform: translate(-25px, 35px) rotate(90deg); opacity: 0.7; }
            66% { transform: translate(35px, -25px) rotate(180deg); opacity: 0.5; }
          }

          @keyframes float2 {
            0%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 0.5; }
            33% { transform: translate(20px, 30px) rotate(150deg); opacity: 0.3; }
            66% { transform: translate(-30px, -20px) rotate(300deg); opacity: 0.6; }
          }
        `}</style>
      </>
    );
  };
  
  // Trial Badge Component - shows on all screens for trial users
  const TrialBadge = () => {
    if (agentInfo?.agentInfo?.plan !== 'Free Trial') return null;
    
    return (
      <div style={{
        position: 'fixed',
        top: '16px',
        right: '16px',
        backgroundColor: 'rgba(99, 102, 241, 0.35)',
        border: '1px solid rgba(99, 102, 241, 0.5)',
        borderRadius: '8px',
        padding: '8px 14px',
        zIndex: 1000,
        backdropFilter: 'blur(8px)',
        cursor: 'pointer',
        transition: 'all 0.2s'
      }}
      onClick={() => window.open('https://buy.stripe.com/eVqaEY1u80Hmdq6dXK3F600', '_blank')}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.5)';
        e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.7)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.35)';
        e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.5)';
      }}
      title="Click to activate your account"
      >
        <span style={{
          color: '#ffffff',
          fontSize: '13px',
          fontWeight: '500',
          letterSpacing: '0.3px'
        }}>
          Trial Account
        </span>
      </div>
    );
  };

  // Main dashboard when authenticated
  if (activeTab === 'dashboard') {
    return (
      <>
        <style>{animationStyles}</style>
        <TrialBadge />
        <TrialWelcomeModal />
        <OnboardingGate />
        <AgentLayout
          activeTab="dashboard"
          onDashboard={handleDashboard}
          onMyListings={handleMyListings}
          onAddProperty={handleAddNewProperty}
          onInvitations={handleInvitations}
          onShowings={handleShowings}
          onConversations={handleConversations}
          onAvailableLeads={handleAvailableLeads}
          onTeamRoster={handleTeamRoster}
          onMediaLibrary={handleMediaLibrary}
          onSupportHub={handleSupportHub}
          onLogout={handleLogout}
        >
          <div className={isTransitioning ? 'page-transition-out' : 'page-transition'}>
            <AgentDashboard 
              agentInfo={agentInfo} 
              propertyCount={propertyCount}
              onAvailableLeads={handleAvailableLeads}
              onInvitations={handleInvitations}
              onShowings={handleShowings}
            />
          </div>
        </AgentLayout>
      </>
    );
  }
  
  // My Listings Tab
  if (activeTab === 'listings') {
    return (
      <>
        <style>{animationStyles}</style>
        <TrialBadge />
        <OnboardingGate />
        <AgentLayout 
          activeTab="listings"
          onDashboard={handleDashboard}
          onMyListings={handleMyListings}
          onAddProperty={handleAddNewProperty}
          onInvitations={handleInvitations}
          onShowings={handleShowings}
          onConversations={handleConversations}
          onAvailableLeads={handleAvailableLeads}
          onTeamRoster={handleTeamRoster}
          onMediaLibrary={handleMediaLibrary}
          onSupportHub={handleSupportHub}
          onLogout={handleLogout}
        >
          <div className={isTransitioning ? 'page-transition-out' : 'page-transition'}>
            <AgentPropertyList 
              agentId={agentInfo._id}
              onEditProperty={handleEditProperty}
              onPropertyChanged={fetchAgentPropertyCount}
              onAddProperty={handleAddNewProperty}
            />
          </div>
        </AgentLayout>
      </>
    );
  }
  
  // My Showings Tab
  if (activeTab === 'showings') {
    return (
      <>
        <style>{animationStyles}</style>
        <TrialBadge />
        <OnboardingGate />
        <AgentLayout 
          activeTab="showings"
          onDashboard={handleDashboard}
          onMyListings={handleMyListings}
          onAddProperty={handleAddNewProperty}
          onInvitations={handleInvitations}
          onShowings={handleShowings}
          onConversations={handleConversations}
          onAvailableLeads={handleAvailableLeads}
          onTeamRoster={handleTeamRoster}
          onMediaLibrary={handleMediaLibrary}
          onSupportHub={handleSupportHub}
          onLogout={handleLogout}
        >
          <div className={isTransitioning ? 'page-transition-out' : 'page-transition'}>
            <AgentShowingsTab 
              agentInfo={agentInfo}
              onShowingChanged={() => {
                // Any callback needed when showings are updated
              }}
            />
          </div>
        </AgentLayout>
      </>
    );
  }

  // Media Library Tab — videos today, social posts and other assets later
  if (activeTab === 'media') {
    return (
      <>
        <style>{animationStyles}</style>
        <TrialBadge />
        <OnboardingGate />
        <AgentLayout
          activeTab="media"
          onDashboard={handleDashboard}
          onMyListings={handleMyListings}
          onAddProperty={handleAddNewProperty}
          onInvitations={handleInvitations}
          onShowings={handleShowings}
          onConversations={handleConversations}
          onAvailableLeads={handleAvailableLeads}
          onTeamRoster={handleTeamRoster}
          onMediaLibrary={handleMediaLibrary}
          onSupportHub={handleSupportHub}
          onLogout={handleLogout}
        >
          <div className={isTransitioning ? 'page-transition-out' : 'page-transition'}>
            <MediaLibrary />
          </div>
        </AgentLayout>
      </>
    );
  }
  
  // Add Property Tab
  if (activeTab === 'add') {
    return (
      <>
        <style>{animationStyles}</style>
        <TrialBadge />
        <OnboardingGate />
        <AgentLayout 
          activeTab="add"
          onDashboard={handleDashboard}
          onMyListings={handleMyListings}
          onAddProperty={handleAddNewProperty}
          onInvitations={handleInvitations}
          onShowings={handleShowings}
          onConversations={handleConversations}
          onAvailableLeads={handleAvailableLeads}
          onTeamRoster={handleTeamRoster}
          onMediaLibrary={handleMediaLibrary}
          onSupportHub={handleSupportHub}
          onLogout={handleLogout}
        >
          <div className={isTransitioning ? 'page-transition-out' : 'page-transition'}>
            <AgentPropertyForm 
              agentInfo={agentInfo}
              onPropertyAdded={handlePropertyAdded}
            />
          </div>
        </AgentLayout>
      </>
    );
  }
  
  // Invitations Tab
  if (activeTab === 'invitations') {
    return (
      <>
        <style>{animationStyles}</style>
        <TrialBadge />
        <OnboardingGate />
        <AgentLayout
          activeTab="invitations"
          onDashboard={handleDashboard}
          onMyListings={handleMyListings}
          onAddProperty={handleAddNewProperty}
          onInvitations={handleInvitations}
          onShowings={handleShowings}
          onConversations={handleConversations}
          onAvailableLeads={handleAvailableLeads}
          onTeamRoster={handleTeamRoster}
          onMediaLibrary={handleMediaLibrary}
          onSupportHub={handleSupportHub}
          onLogout={handleLogout}
        >
          <div className={isTransitioning ? 'page-transition-out' : 'page-transition'}>
            <AgentInvitations agentInfo={agentInfo} />
          </div>
        </AgentLayout>
      </>
    );
  }

  // Conversations Tab
  if (activeTab === 'conversations') {
    return (
      <>
        <style>{animationStyles}</style>
        <TrialBadge />
        <OnboardingGate />
        <AgentLayout
          activeTab="conversations"
          onDashboard={handleDashboard}
          onMyListings={handleMyListings}
          onAddProperty={handleAddNewProperty}
          onInvitations={handleInvitations}
          onShowings={handleShowings}
          onConversations={handleConversations}
          onAvailableLeads={handleAvailableLeads}
          onTeamRoster={handleTeamRoster}
          onMediaLibrary={handleMediaLibrary}
          onSupportHub={handleSupportHub}
          onLogout={handleLogout}
        >
          <div className={isTransitioning ? 'page-transition-out' : 'page-transition'}>
            <AgentConversations agentInfo={agentInfo} onInvitations={handleInvitations} />
          </div>
        </AgentLayout>
      </>
    );
  }

  // Available Leads Tab
  if (activeTab === 'available-leads') {
    return (
      <>
        <style>{animationStyles}</style>
        <TrialBadge />
        <OnboardingGate />
        <AgentLayout
          activeTab="available-leads"
          onDashboard={handleDashboard}
          onMyListings={handleMyListings}
          onAddProperty={handleAddNewProperty}
          onInvitations={handleInvitations}
          onShowings={handleShowings}
          onConversations={handleConversations}
          onAvailableLeads={handleAvailableLeads}
          onTeamRoster={handleTeamRoster}
          onMediaLibrary={handleMediaLibrary}
          onSupportHub={handleSupportHub}
          onLogout={handleLogout}
        >
          <div className={isTransitioning ? 'page-transition-out' : 'page-transition'}>
            <AvailableLeads agentInfo={agentInfo} />
          </div>
        </AgentLayout>
      </>
    );
  }

  // Team Roster Tab
  if (activeTab === 'team-roster') {
    return (
      <>
        <style>{animationStyles}</style>
        <TrialBadge />
        <OnboardingGate />
        <AgentLayout
          activeTab="team-roster"
          onDashboard={handleDashboard}
          onMyListings={handleMyListings}
          onAddProperty={handleAddNewProperty}
          onInvitations={handleInvitations}
          onShowings={handleShowings}
          onConversations={handleConversations}
          onAvailableLeads={handleAvailableLeads}
          onTeamRoster={handleTeamRoster}
          onMediaLibrary={handleMediaLibrary}
          onSupportHub={handleSupportHub}
          onLogout={handleLogout}
        >
          <div className={isTransitioning ? 'page-transition-out' : 'page-transition'}>
            <AgentTeamRoster agentInfo={agentInfo} />
          </div>
        </AgentLayout>
      </>
    );
  }

  // Support Hub Tab
  if (activeTab === 'support-hub') {
    return (
      <>
        <style>{animationStyles}</style>
        <TrialBadge />
        <div className={isTransitioning ? 'page-transition-out' : 'page-transition'}>
          <SupportHub
            agentInfo={agentInfo}
            onDashboard={handleDashboard}
            onMyListings={handleMyListings}
            onAddProperty={handleAddNewProperty}
            onLogout={handleLogout}
            onInvitations={handleInvitations}
            onShowings={handleShowings}
            onConversations={handleConversations}
            onAvailableLeads={handleAvailableLeads}
            onTeamRoster={handleTeamRoster}
          onMediaLibrary={handleMediaLibrary}
            onSupportHub={handleSupportHub}
            onNavigateToCategory={handleSupportCategory}
            onNavigateToArticle={handleSupportArticle}
          />
        </div>
      </>
    );
  }

  // Support Category Tab
  if (activeTab === 'support-category' && supportCategory) {
    return (
      <>
        <style>{animationStyles}</style>
        <TrialBadge />
        <div className={isTransitioning ? 'page-transition-out' : 'page-transition'}>
          <SupportCategory
            categorySlug={supportCategory}
            agentInfo={agentInfo}
            onDashboard={handleDashboard}
            onMyListings={handleMyListings}
            onAddProperty={handleAddNewProperty}
            onLogout={handleLogout}
            onInvitations={handleInvitations}
            onShowings={handleShowings}
            onConversations={handleConversations}
            onAvailableLeads={handleAvailableLeads}
            onTeamRoster={handleTeamRoster}
          onMediaLibrary={handleMediaLibrary}
            onSupportHub={handleSupportHub}
            onNavigateToHub={handleSupportHub}
            onNavigateToArticle={handleSupportArticle}
          />
        </div>
      </>
    );
  }

  // Support Article Tab
  if (activeTab === 'support-article' && supportCategory && supportArticle) {
    return (
      <>
        <style>{animationStyles}</style>
        <TrialBadge />
        <div className={isTransitioning ? 'page-transition-out' : 'page-transition'}>
          <SupportArticle
            categorySlug={supportCategory}
            articleSlug={supportArticle}
            agentInfo={agentInfo}
            onDashboard={handleDashboard}
            onMyListings={handleMyListings}
            onAddProperty={handleAddNewProperty}
            onLogout={handleLogout}
            onInvitations={handleInvitations}
            onShowings={handleShowings}
            onConversations={handleConversations}
            onAvailableLeads={handleAvailableLeads}
            onTeamRoster={handleTeamRoster}
          onMediaLibrary={handleMediaLibrary}
            onSupportHub={handleSupportHub}
            onNavigateToHub={handleSupportHub}
            onNavigateToCategory={handleSupportCategory}
            onNavigateToArticle={handleSupportArticle}
          />
        </div>
      </>
    );
  }

  // Edit Property Tab
  if (activeTab === 'edit' && currentProperty) {
    return (
      <>
        <style>{animationStyles}</style>
        <TrialBadge />
        <OnboardingGate />
        <AgentLayout 
          activeTab="edit"
          onDashboard={handleDashboard}
          onMyListings={handleMyListings}
          onAddProperty={handleAddNewProperty}
          onInvitations={handleInvitations}
          onShowings={handleShowings}
          onConversations={handleConversations}
          onAvailableLeads={handleAvailableLeads}
          onTeamRoster={handleTeamRoster}
          onMediaLibrary={handleMediaLibrary}
          onSupportHub={handleSupportHub}
          onLogout={handleLogout}
        >
          <div className={isTransitioning ? 'page-transition-out' : 'page-transition'}>
            <AgentPropertyForm 
              agentInfo={agentInfo}
              property={currentProperty}
              onPropertyAdded={handlePropertyAdded}
            />
          </div>
        </AgentLayout>
      </>
    );
  }
  
  // Fallback
  return (
    <div className="loading-state" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <p>Something went wrong. Please try again.</p>
    </div>
  );
};

export default AgentPortal;